<template>
  <div
    class="default-article"
    :class="template"
  >
    <div class="default-article__main">
      <div class="default-article__analytics">
        <div
          v-if="isShowArticleCard"
          class="default-article__header"
        >
          <ArticleCard :article="article" />
        </div>
        <div class="default-article__content default-article__content--article-content">
          <div
            :class="{
              'default-article__content-wrapper--page': !isShowArticleCard,
            }"
            class="default-article__content-wrapper"
          >
            <h1
              v-if="!isShowArticleCard && article"
              class="default-article__headline"
            >
              {{ article.title }}
            </h1>
            <template
              v-for="(content, index) in slicedContents"
              :key="index"
            >
              <TheContent
                v-for="item of content"
                :key="item.id"
                :elem="item"
              />
              <WidgetsArea
                v-if="index === 0"
                area="after-first-paragraph"
              />
              <!-- виджет не должен быть показан в конце, если это последний чанк -->
              <WidgetsArea
                v-else-if="index !== slicedContents.length - 1"
                :area="'post-dynamic'"
                :index-sliced-content="index"
              />
            </template>
          </div>
          <ArticleFooter
            v-if="isShowArticleCard"
            class="default-article__footer"
          />
        </div>
      </div>
      <div
        v-if="isShowBottomPart"
        class="default-article__content default-article__content--bottom"
      >
        <div class="default-article__content-wrapper">
          <WidgetsArea area="above-comments" />

          <!-- комментарии -->
          <CommentsContainer
            v-if="showComments"
            :id="article.id"
            class="default-article__comments comments"
          />

          <WidgetsArea area="below-comments" />
        </div>
      </div>
    </div>
    <Sidebar
      v-if="!isOneColumn"
      class="default-article__sidebar"
    />
  </div>
</template>

<script lang="ts">
  import TheContent from '~/components/TheContent';
  import ArticleCard from '~/components/ArticleCard.vue';
  import { BREAKPOINTS } from '~/constants';
  import type { IFullArticle, TContent } from '~/types/common';
  import ArticleFooter from '~/components/ArticleFooter.vue';
  import Sidebar from '~/components/Sidebar.vue';
  import WidgetsArea from '~/components/WidgetsArea/WidgetsArea.vue';
  import CommentsContainer from '~/components/CommentsContainer.vue';
  import { useSingleArticleStore } from '~/store/singleArticle';
  import { getSplicedContent } from '@devhacker/shared/utils/adfox';

  export default defineNuxtComponent({
    name: 'DefaultArticle',

    components: {
      TheContent,
      ArticleCard,
      ArticleFooter,
      Sidebar,
      WidgetsArea,
      CommentsContainer,
    },

    provide() {
      return {
        isEndArticleReadAlso: () => {
          return Boolean(this.article?.parsed_content?.at(-1)?.type === 'burninghut__read-also');
        },
      };
    },

    data: () => ({
      BREAKPOINTS,
    }),

    computed: {
      isShowBottomPart(): boolean {
        return !!this.article && !this.isPage;
      },

      article() {
        const article = useSingleArticleStore().article;

        if (!article) {
          throw createError({ status: 500, message: `Article hasn't been provided from pinia` });
        }

        return article as IFullArticle;
      },

      slicedContents(): TContent[][] {
        const spliced = getSplicedContent(this.article.parsed_content, 'bh-kiss__paragraph', 7);
        return spliced;
      },

      isShowArticleCard(): boolean {
        return this.article?.type === 'post';
      },

      subtitle(): TContent | null {
        if (this.article?.parsed_content) {
          return this.article.parsed_content[0];
        }
        return null;
      },

      showComments(): boolean | undefined {
        return this.article?.comments_enabled && !this.article?.is_promo;
      },

      isOneColumn(): boolean {
        return this.article?.template === 'one-column';
      },

      isPage(): boolean {
        return this.article?.type === 'page';
      },

      isTwoColumns(): boolean {
        return (
          this.article?.template === 'two-column' ||
          this.article?.template === 'default' ||
          this.article?.template === ''
        );
      },

      template(): Record<string, boolean | undefined> {
        const className = 'default-article';
        return {
          [`${className}--one-column`]: this.isOneColumn,
          [`${className}--two-columns`]: this.isTwoColumns,
          [`${className}--page`]: this.isPage,
        };
      },
    },
  });
</script>

<style lang="scss" scoped>
  @use '~/scss/article' as *;

  .default-article {
    @include container;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    padding-bottom: $spacing-8;
    background: #fff;
    @include tablet {
      padding-bottom: $spacing-10;
      margin-top: 24px !important;
    }

    @include big-tablet {
      max-width: unset;
      grid-template-columns: minmax(0, 884px) 300px !important;
    }

    @include desktop {
      max-width: 1280px;
    }

    @include header-margin;

    &--page {
      padding-top: $spacing-6;
      @include big-tablet {
        padding-top: $spacing-8;
      }
      @include desktop {
        padding-top: $spacing-9;
      }
    }

    &__main {
      font-family: $text-font--1;
    }
    &__content {
      box-sizing: content-box;
      margin: 0 auto;
      max-width: 860px;

      @include big-tablet {
        padding: 0 $spacing-6;
        box-sizing: border-box;
        max-width: 780px;
      }

      &__footer {
        margin: $spacing-6 auto;
      }

      &--bottom {
        margin-top: $spacing-8;
        @include tablet {
          margin-top: $spacing-10;
        }
      }
    }

    &__footer {
      padding-left: $spacing-4;
      padding-right: $spacing-4;
      @include tablet {
        padding: 0 $spacing-16;
      }
      @include big-tablet {
        padding: 0;
      }
      margin: 48px auto 0;
    }

    &__content-wrapper :deep() > {
      @include article-elems;
    }

    &__content-wrapper:not(.default-article__content-wrapper--page) :deep() > {
      @include lead-styles;
    }

    &__content-wrapper {
      padding-left: $spacing-4;
      padding-right: $spacing-4;

      @include tablet {
        padding: 0 $spacing-16;
      }
      @include big-tablet {
        padding: 0;
      }

      @include article-picture-styles;
    }

    &__headline {
      margin-top: $spacing-2 !important;
      font-family: $headline-font--1;
      font-weight: 400;
      @include headline-normal-text;
      margin-bottom: $spacing-3 !important;

      @include tablet {
        margin-top: $spacing-4 !important;
        @include headline-big-text;
      }
    }

    @include sidebar-styles;
  }

  .comments {
    padding: 0;
    margin-top: 0 !important;
    margin-left: auto !important;
    margin-right: auto !important;
    &:not(:last-child) {
      margin-bottom: $spacing-8 !important;
      @include tablet {
        margin-bottom: $spacing-10 !important;
      }
    }

    :deep() .lh-talker-app {
      margin-top: 0;
      margin-bottom: 0;

      .form {
        > *:first-child {
          border: 2px solid $stroke-color--1;

          @include hover {
            border-color: #699bdc;
          }
        }
      }
    }
  }

  .default-article--two-columns.default-article {
    @include big-tablet {
      display: grid;
      grid-template-columns: auto 300px;
      grid-column-gap: $spacing-5;
      max-width: unset;
    }

    @include desktop {
      grid-column-gap: $spacing-6;
      max-width: 1280px;
    }
  }

  .default-article--two-columns .default-article {
    &__main {
      @include desktop {
        max-width: 884px;
        width: 100%;
      }
    }
  }

  .default-article--one-column.default-article {
    @include desktop {
      padding: $spacing-9 $spacing-12 $spacing-10;
    }
  }

  .default-article--one-column .default-article {
    &__content {
      margin-left: auto;
      margin-right: auto;

      @include big-tablet {
        max-width: 732px;
        box-sizing: content-box;
      }

      &--the-content {
        @include tablet {
          padding: 0;
        }
      }
    }
  }
</style>
